// [檔案： src/js/utils.js]

import { dom } from './dom.js';
import { t, currentLang } from './i18n.js';
import { showToast } from './toast.js';

import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

/**
 * 將交易數據導出為 CSV 並觸發分享或下載
 * @param {Array} transactions 
 */
export async function exportToCSV(transactions) {
    if (!transactions || transactions.length === 0) return;

    // 1. 生成 CSV 內容
    const headers = ['Date', 'Type', 'Category', 'Amount', 'Description'];
    const rows = transactions.map(tx => [
        tx.date,
        t(tx.type),
        t(tx.category || 'cat_general'),
        tx.amount,
        tx.description || ''
    ]);

    // 處理 CSV 轉義與 BOM (解決 Excel 亂碼)
    const csvContent = "\ufeff" + [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const fileName = `cashbook_export_${new Date().toISOString().slice(0, 10)}.csv`;

    try {
        if (Capacitor.isNativePlatform()) {
            // 2a. 原生平台：寫入檔案並分享
            const result = await Filesystem.writeFile({
                path: fileName,
                data: csvContent,
                directory: Directory.Cache, // 使用 Cache 目錄，導出後系統會清理
                encoding: Encoding.UTF8
            });

            await Share.share({
                title: t('exportData'),
                text: t('exportSuccess'),
                url: result.uri,
                dialogTitle: t('exportData')
            });
        } else {
            // 2b. Web 平台：直接下載
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', fileName);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            showMessage(t('exportSuccess'));
        }
    } catch (error) {
        console.error('Export failed:', error);
        showMessage(t('exportFailed').replace('{error}', error.message), 'error');
    }
}

/**
 * 顯示一個訊息提示 (Toast)
 * @param {string} message 
 * @param {'success' | 'error'} type 
 */
export function showMessage(message, type = 'success') {
    showToast(message, type);
}

/**
 * 顯示一個確認彈窗
 * @param {string} message 
 * @param {Function} onConfirm 
 * @param {Function} [onCancel] 
 */
export function showConfirm(message, onConfirm, onCancel) {
    dom.messageText.textContent = message;
    dom.closeModalBtn.style.display = 'none'; // Hide the default close button
    dom.confirmButtons.style.display = 'grid';
    dom.messageModal.style.display = 'flex';
    
    const newOkBtn = dom.confirmOkBtn.cloneNode(true);
    newOkBtn.textContent = t('confirm');
    dom.confirmButtons.replaceChild(newOkBtn, dom.confirmOkBtn);
    dom.confirmOkBtn = newOkBtn; 

    const newCancelBtn = dom.confirmCancelBtn.cloneNode(true);
    newCancelBtn.textContent = t('cancel');
    dom.confirmButtons.replaceChild(newCancelBtn, dom.confirmCancelBtn);
    dom.confirmCancelBtn = newCancelBtn;

    const closeConfirmModal = () => {
        dom.messageModal.style.display = 'none';
        removeClickListener(); // Clean up the listener when closed
    };

    dom.closeModalBtn.onclick = closeConfirmModal;

    newOkBtn.addEventListener('click', () => {
        if (onConfirm) onConfirm();
        closeConfirmModal();
    });
    newCancelBtn.addEventListener('click', () => {
        if (onCancel) onCancel();
        closeConfirmModal();
    });

    const removeClickListener = setupClickOutside(dom.messageModal, dom.messageModalContent, closeConfirmModal);
}

export function formatCurrency(amount) {
    if (typeof amount !== 'number') {
        amount = Number(amount) || 0;
    }
    // 若大於 0 且小於 1 (如 0.2, 0.5)，至少保留 1 位小數，避免被捨入為 0
    const minDigits = (amount > 0 && amount < 1) ? 1 : 0;
    return new Intl.NumberFormat(currentLang === 'en' ? 'en-US' : (currentLang === 'zh-CN' ? 'zh-CN' : 'zh-TW'), {
        style: 'decimal',
        minimumFractionDigits: minDigits,
        maximumFractionDigits: 2
    }).format(amount);
}

/**
 * 將字串中的 HTML 特殊字元進行轉義，以防止 XSS 攻擊。
 * @param {string} str - 要轉義的字串。
 * @returns {string} 轉義後的字串。
 */
export function escapeHTML(str) {
    const div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
}

/**
 * 格式化為 "YYYY年 MM月"
 * @param {Date} date 
 * @returns {string}
 */
export function formatMonthDisplay(date) {
    const lang = currentLang; // ✅ 修正 4： 使用 i18n.js 的當前語言
    if (lang === 'en') {
        return date.toLocaleString('en-US', { year: 'numeric', month: 'long' });
    }
    // 預設 (zh-TW 和 zh-CN)
    return `${date.getFullYear()}年 ${String(date.getMonth() + 1).padStart(2, '0')}月`;
}


/**
 * 格式化為 "YYYY-MM"
 * @param {Date} date 
 * @returns {string}
 */
// export function getYearMonthString(date) {
//     return date.toISOString().slice(0, 7);
// }
/**
 * 獲取本地時區的 YYYY-MM 字串
 * (已修復 toISOString() 造成的時區 Bug)
 * @param {Date} date
 */
export function getYearMonthString(date) {
    // .getFullYear() 會回傳本地時區的年份 (e.g., 2025)
    const year = date.getFullYear();
    
    // .getMonth() 會回傳本地時區的月份 (0-11)
    // 11月1號 -> .getMonth() 回傳 10
    // 這裡的 +1 是必須的，才能將 0-11 轉為 1-12
    const month = date.getMonth() + 1; 
    
    // 補零，將 9 變成 "09", 11 變成 "11"
    const monthString = ('0' + month).slice(-2);
    
    // 回傳 "2025-11"
    return `${year}-${monthString}`;
}

/**
 * 動態應用貨幣和字體大小
 * @param {HTMLElement} el 
 * @param {number} amount 
 * @param {object} sizeClasses 
 */
export function applyCurrency(el, amount, sizeClasses) {
    const formatted = formatCurrency(amount);
    el.textContent = formatted;
    
    const len = formatted.length;
    
    el.classList.remove(sizeClasses.base, sizeClasses.medium, sizeClasses.small);
    if (sizeClasses.xlarge) el.classList.remove(sizeClasses.xlarge);

    if (len > 22) { 
        el.classList.add(sizeClasses.small);
    } else if (len > 15) { 
        el.classList.add(sizeClasses.medium);
    } else {
        el.classList.add(sizeClasses.base);
    }
}

/**
 * Attaches a click event listener to the document to close a modal when clicking outside of it.
 * @param {HTMLElement} modalElement The modal's DOM element (the full screen backdrop).
 * @param {HTMLElement} modalContentElement The modal's content DOM element (the visible box).
 * @param {Function} closeCallback The function to call when an outside click occurs.
 * @returns {Function} A function to remove the attached event listener.
 */
export function setupClickOutside(modalElement, modalContentElement, closeCallback) {
    let removeListener = () => {}; 

    const handler = (event) => {
        // 使用 event.composedPath() 即使元素已從 DOM 移除也能找到它的祖先
        const path = event.composedPath();
        const isFlatpickrClick = path.some(el => el.classList && el.classList.contains('flatpickr-calendar'));
        const isInsideContent = path.includes(modalContentElement);

        if (modalElement.style.display === 'flex' && !isInsideContent && !isFlatpickrClick) {
            closeCallback();
            removeListener();
        }
    };

    // 改用 mousedown 可以在元素被移除前捕捉到點擊
    setTimeout(() => {
        document.addEventListener('mousedown', handler, { capture: true });
    }, 100);

    removeListener = () => {
        document.removeEventListener('mousedown', handler, { capture: true });
    };

    return removeListener;
}